import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, TrendingUp, User, CheckCircle, XCircle, Eye } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface Founder {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

interface Pitch {
  id: number;
  startupName: string;
  shortDescription: string;
  fullDescription: string;
  fundingAmount: number;
  companyName?: string;
  companyWebsite?: string;
  investorOnlyContent?: string;
  labels: string[];
  isPublic: boolean;
  isFunded: boolean;
  fundingDate?: string;
  actualFundingAmount?: number;
  investorInfo?: string;
  createdAt: string;
  updatedAt: string;
  founder: Founder;
}

interface ApiResponse {
  pitches: Pitch[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface FundingFormData {
  actualFundingAmount: string;
  investorInfo: string;
}

const FundingManagement: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, isLightTheme } = useTheme();
  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPitch, setSelectedPitch] = useState<Pitch | null>(null);
  const [showPitchDetails, setShowPitchDetails] = useState(false);
  // const [editingPitch, setEditingPitch] = useState<Pitch | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState<'all' | 'funded' | 'unfunded'>('all');
  const [showFundingDialog, setShowFundingDialog] = useState(false);
  const [fundingPitchId, setFundingPitchId] = useState<number | null>(null);
  const [fundingFormData, setFundingFormData] = useState<FundingFormData>({
    actualFundingAmount: '',
    investorInfo: ''
  });

  useEffect(() => {
    fetchPitches();
  }, [page, filter]);

  const fetchPitches = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (filter !== 'all') {
        params.isFunded = filter === 'funded';
      }
      
      const response = await api.get<ApiResponse>('/admin/pitches', { params });
      
      const { pitches: newPitches, pagination } = response.data;
      
      if (page === 1) {
        setPitches(newPitches);
      } else {
        setPitches(prev => [...prev, ...newPitches]);
      }
      
      setHasMore(pagination.hasNext);
    } catch (error) {
      console.error('Error fetching pitches:', error);
      toast.error('Failed to load pitches');
    } finally {
      setLoading(false);
    }
  };

  const handleFundingUpdate = async (pitchId: number, isFunded: boolean, actualAmount?: number, investorInfo?: string) => {
    try {
      const updateData: any = { isFunded };
      if (isFunded) {
        updateData.fundingDate = new Date().toISOString();
        updateData.actualFundingAmount = actualAmount;
        updateData.investorInfo = investorInfo;
      } else {
        // updateData.fundingDate = null;
        // updateData.actualFundingAmount = null;
        // updateData.investorInfo = null;
      }

      await api.put(`/admin/pitches/${pitchId}/funding`, updateData);
      toast.success(`Pitch ${isFunded ? 'marked as funded' : 'marked as unfunded'}`);
      
      // Refresh the pitches list
      setPage(1);
      fetchPitches();
    } catch (error) {
      console.error('Error updating funding status:', error);
      toast.error('Failed to update funding status');
    }
  };

  const openFundingDialog = (pitchId: number) => {
    setFundingPitchId(pitchId);
    setFundingFormData({
      actualFundingAmount: '',
      investorInfo: ''
    });
    setShowFundingDialog(true);
  };

  const closeFundingDialog = () => {
    setShowFundingDialog(false);
    setFundingPitchId(null);
    setFundingFormData({
      actualFundingAmount: '',
      investorInfo: ''
    });
  };

  const handleFundingSubmit = async () => {
    if (!fundingPitchId) return;

    const actualAmount = parseFloat(fundingFormData.actualFundingAmount);
    if (!actualAmount || actualAmount <= 0) {
      toast.error('Please enter a valid funding amount');
      return;
    }

    if (!fundingFormData.investorInfo.trim()) {
      toast.error('Please enter investor information');
      return;
    }

    await handleFundingUpdate(
      fundingPitchId, 
      true, 
      actualAmount, 
      fundingFormData.investorInfo.trim()
    );
    closeFundingDialog();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFundingStatusColor = (isFunded: boolean) => {
    return isFunded 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  return (
    <Layout title="Funding Management" showBackButton onBack={() => navigate('/')}>

      {/* Filter Tabs */}
      <div className={`${themeClasses.card} border-b ${themeClasses.border} p-4 rounded-lg`}>
        <div className="flex items-center space-x-2">
          <TrendingUp className={`w-4 h-4 ${themeClasses.textSecondary}`} />
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-primary-500 text-white'
                : isLightTheme 
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('funded')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'funded'
                ? 'bg-green-500 text-white'
                : isLightTheme 
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Funded
          </button>
          <button
            onClick={() => setFilter('unfunded')}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === 'unfunded'
                ? 'bg-yellow-500 text-white'
                : isLightTheme 
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Unfunded
          </button>
        </div>
      </div>

      {/* Pitches List */}
      <div className="p-4 space-y-4">
        {pitches.map((pitch) => (
          <div key={pitch.id} className={`${themeClasses.card} rounded-lg`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                {/* Founder Avatar */}
                <div className={`w-12 h-12 ${isLightTheme ? 'bg-gray-300' : 'bg-gray-600'} rounded-full flex items-center justify-center flex-shrink-0`}>
                  {pitch.founder.profilePictureUrl ? (
                    <img
                      src={pitch.founder.profilePictureUrl}
                      alt={`${pitch.founder.firstName} ${pitch.founder.lastName}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <User className={`w-6 h-6 ${themeClasses.textSecondary}`} />
                  )}
                </div>

                {/* Pitch Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{pitch.startupName}</h3>
                      <p className={`${themeClasses.textSecondary} text-sm`}>
                        by {pitch.founder.firstName} {pitch.founder.lastName}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-primary-400">
                        {formatCurrency(pitch.fundingAmount)}
                      </p>
                      <p className={`${themeClasses.textSecondary} text-sm`}>Funding Goal</p>
                    </div>
                  </div>

                  <p className={`${themeClasses.textSecondary} text-sm mb-3`}>{pitch.shortDescription}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      {pitch.labels.map((label) => (
                        <span
                          key={label}
                          className={`px-2 py-1 ${isLightTheme ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300'} text-xs rounded-full`}
                        >
                          {label}
                        </span>
                      ))}
                      <span className={`px-2 py-1 text-xs rounded-full border ${getFundingStatusColor(pitch.isFunded)}`}>
                        {pitch.isFunded ? 'Funded' : 'Seeking Funding'}
                      </span>
                    </div>
                    <span className={`${themeClasses.textSecondary} text-xs`}>
                      {formatDate(pitch.createdAt)}
                    </span>
                  </div>

                  {pitch.isFunded && pitch.actualFundingAmount && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`${themeClasses.textSecondary} text-sm`}>
                            <span className="font-medium">Actual Funding:</span> {formatCurrency(pitch.actualFundingAmount)}
                          </p>
                          {pitch.investorInfo && (
                            <p className={`${themeClasses.textSecondary} text-sm`}>
                              <span className="font-medium">Investor:</span> {pitch.investorInfo}
                            </p>
                          )}
                        </div>
                        <span className={`${themeClasses.textSecondary} text-xs`}>
                          {pitch.fundingDate && formatDate(pitch.fundingDate)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => {
                    setSelectedPitch(pitch);
                    setShowPitchDetails(true);
                  }}
                  className={`p-2 ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors rounded-lg`}
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => {
                    if (pitch.isFunded) {
                      // Mark as unfunded
                      handleFundingUpdate(pitch.id, false);
                    } else {
                      // Show funding modal
                      openFundingDialog(pitch.id);
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    pitch.isFunded 
                      ? 'text-red-400 hover:text-red-300' 
                      : 'text-green-400 hover:text-green-300'
                  }`}
                  title={pitch.isFunded ? 'Mark as Unfunded' : 'Mark as Funded'}
                >
                  {pitch.isFunded ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Load More Button */}
        {hasMore && (
          <div className="text-center">
            <button
              onClick={() => setPage(prev => prev + 1)}
              disabled={loading}
              className={`${themeClasses.buttonSecondary} px-6 py-2 rounded-lg font-medium transition-colors duration-200`}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && pitches.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className={`w-16 h-16 ${themeClasses.textSecondary} mx-auto mb-4`} />
            <p className={themeClasses.textSecondary}>No pitches found</p>
          </div>
        )}
      </div>

      {/* Pitch Details Modal */}
      {showPitchDetails && selectedPitch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Pitch Details</h2>
              <button
                onClick={() => setShowPitchDetails(false)}
                className={`p-2 ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors rounded-lg`}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Startup Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Startup Name</label>
                    <p className={themeClasses.text}>{selectedPitch.startupName}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Funding Goal</label>
                    <p className="text-lg font-semibold text-primary-400">{formatCurrency(selectedPitch.fundingAmount)}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Founder</label>
                    <p className={themeClasses.text}>{selectedPitch.founder.firstName} {selectedPitch.founder.lastName}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Status</label>
                    <span className={`px-2 py-1 text-xs rounded-full border ${getFundingStatusColor(selectedPitch.isFunded)}`}>
                      {selectedPitch.isFunded ? 'Funded' : 'Seeking Funding'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Description</h3>
                <p className={`${themeClasses.textSecondary} leading-relaxed`}>{selectedPitch.fullDescription}</p>
              </div>

              {/* Funding Details */}
              {selectedPitch.isFunded && (
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Funding Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedPitch.actualFundingAmount && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Actual Funding</label>
                        <p className="text-lg font-semibold text-green-400">{formatCurrency(selectedPitch.actualFundingAmount)}</p>
                      </div>
                    )}
                    {selectedPitch.investorInfo && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Investor</label>
                        <p className={themeClasses.text}>{selectedPitch.investorInfo}</p>
                      </div>
                    )}
                    {selectedPitch.fundingDate && (
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Funding Date</label>
                        <p className={themeClasses.text}>{formatDate(selectedPitch.fundingDate)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Labels */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Labels</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPitch.labels.map((label) => (
                    <span
                      key={label}
                      className={`px-3 py-1 ${isLightTheme ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300'} text-sm rounded-full`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Timeline</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Created</label>
                    <p className={themeClasses.text}>{formatDate(selectedPitch.createdAt)}</p>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>Last Updated</label>
                    <p className={themeClasses.text}>{formatDate(selectedPitch.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Funding Dialog */}
      {showFundingDialog && fundingPitchId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-md w-full max-h-[90vh] overflow-y-auto rounded-lg`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Mark as Funded</h2>
              <button
                onClick={closeFundingDialog}
                className={`p-2 ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors rounded-lg`}
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Actual Funding Amount (USD)
                </label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}>
                    $
                  </span>
                  <input
                    type="number"
                    value={fundingFormData.actualFundingAmount}
                    onChange={(e) => setFundingFormData(prev => ({ ...prev, actualFundingAmount: e.target.value }))}
                    className={`${themeClasses.input} w-full pl-8 pr-4 py-3 text-lg font-medium`}
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                <p className={`${themeClasses.textSecondary} text-xs mt-1`}>
                  Enter the actual amount received from investors
                </p>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Investor Information
                </label>
                <textarea
                  value={fundingFormData.investorInfo}
                  onChange={(e) => setFundingFormData(prev => ({ ...prev, investorInfo: e.target.value }))}
                  className={`${themeClasses.input} w-full h-24 resize-none p-4`}
                  placeholder="Enter investor details, company names, or funding round information..."
                  rows={4}
                />
                <p className={`${themeClasses.textSecondary} text-xs mt-1`}>
                  Include investor names, companies, or funding round details
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeFundingDialog}
                  className={`${themeClasses.buttonSecondary} px-6 py-3 rounded-lg font-medium transition-colors duration-200`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFundingSubmit}
                  className={`${themeClasses.button} px-6 py-3 rounded-lg font-medium transition-colors duration-200`}
                >
                  Mark as Funded
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FundingManagement; 